import React, { useEffect, useState } from "react"
import { Backdrop, CircularProgress, Container, Grid, Snackbar } from "@material-ui/core"
import { Alert } from "@material-ui/lab"
import { useParams } from "react-router-dom"
import backdropStyles from '../AgentSkillsManagement/styles.module.css'
import mainStyles from '../WorkerManagementComponent/WorkerManagementComponent.module.css'
import styles from './styles.module.css'
import { BlueInverseBtn } from "../Styled/StyledButtons"
import {
    DestinationDetail,
    DestinationHistory,
    actionLabel,
    isRemoved,
} from "../../models/ExemptDestinations/ExemptDestination"
import { ExemptDestinationsActions } from "../../actions/ExemptDestinationsActions"

/**
 * One exempt number: its current state, whether the exemption is actually live, and every
 * change ever made to it.
 *
 * The enforcement badge is the point of this page. A row in MySQL is not what the inbound
 * webhook reads — a cached set is — and "the admin says exempt but calls are still being
 * filtered" cannot be diagnosed from the list. Active-but-not-live means the cache did not
 * take the last write, and the fix is to re-save the entry.
 */
const NumberDetail = () => {
    const { phone } = useParams<{ phone: string }>()
    const [detail, setDetail] = useState<DestinationDetail | null>(null)
    const [backdrop, setBackdrop] = useState(true)
    const [error, setError] = useState<string | null>(null)

    useEffect(() => {
        ExemptDestinationsActions.getDestination(phone)
            .then((data: any) => {
                setDetail(data)
                setBackdrop(false)
            })
            .catch((err) => {
                setError(err?.response?.data?.error || err?.response?.data?.message || err?.message)
                setBackdrop(false)
            })
    }, [phone])

    const destination = detail ? detail.destination : null
    const removed = destination ? isRemoved(destination) : false
    const live = detail ? detail.enforcement.live_in_cache : false

    return (
        <>
            <Container maxWidth={false} className={mainStyles.mainContainer}>
                <div className={styles.DetailHead}>
                    <Grid container spacing={2} alignItems="center">
                        <Grid item xs={12} md={4}>
                            <div className={styles.DetailPhone}>
                                {destination ? destination.phone_e164 : phone}
                            </div>
                            <div className={styles.Muted}>
                                {destination ? destination.comment : ''}
                            </div>
                        </Grid>
                        <Grid item xs={12} md={4}>
                            {destination === null ? null :
                                removed ?
                                    <span className={`${styles.Badge} ${styles.BadgeRemoved}`}>Removed</span>
                                    : live ?
                                        <span className={`${styles.Badge} ${styles.BadgeActive}`}>Exempt and live</span>
                                        :
                                        <span className={`${styles.Badge} ${styles.BadgeStale}`}>Exempt but NOT live</span>
                            }
                            {destination !== null && !removed && !live ?
                                <div className={styles.Hint}>
                                    The row is active but the cached set the call handler reads
                                    does not contain it. Re-save the reason on the list screen
                                    to republish it.
                                </div>
                                : null}
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <BlueInverseBtn variant="contained" fullWidth={true} href="/exempt-destinations">
                                Back to the list
                            </BlueInverseBtn>
                        </Grid>
                    </Grid>
                </div>

                <div className={styles.Panel}>
                    <Grid container spacing={2}>
                        <Grid className={mainStyles.tableTitle} item xs={3}>When</Grid>
                        <Grid className={mainStyles.tableTitle} item xs={3}>Change</Grid>
                        <Grid className={mainStyles.tableTitle} item xs={3}>Reason</Grid>
                        <Grid className={mainStyles.tableTitle} item xs={3}>By</Grid>
                    </Grid>
                    {(destination?.history || []).map((row: DestinationHistory) => (
                        <Grid container spacing={2} key={row.id} className={styles.HistoryRow}>
                            <Grid item xs={3}>
                                <span className={styles.Muted}>{row.created_at}</span>
                            </Grid>
                            <Grid item xs={3}>
                                <span className={styles.Comment}>{actionLabel(row.action)}</span>
                            </Grid>
                            <Grid item xs={3}>
                                <span className={styles.Comment}>
                                    {row.action ? row.action.comment : ''}
                                </span>
                            </Grid>
                            <Grid item xs={3}>
                                <span className={styles.Muted}>
                                    {row.user ? row.user.email : 'system'}
                                </span>
                            </Grid>
                        </Grid>
                    ))}
                    {destination !== null && (destination.history || []).length === 0 ?
                        <div className={styles.Muted}>
                            No recorded changes — this number was seeded when the list moved out
                            of the code.
                        </div>
                        : null}
                </div>
            </Container>

            <Snackbar
                open={error !== null}
                autoHideDuration={5000}
                onClose={() => setError(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            >
                <Alert onClose={() => setError(null)} severity="error" variant="filled">
                    <div className={backdropStyles.AlertError}>{error}</div>
                </Alert>
            </Snackbar>

            <Backdrop className={backdropStyles.Backdrop} open={backdrop}>
                <CircularProgress className={backdropStyles.BackdropProgress} />&nbsp;&nbsp;
                <p className={backdropStyles.BackdropProgress}>Loading...</p>
            </Backdrop>
        </>
    )
}

export default NumberDetail
